import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { formatMoneyInr } from '../../utils/moneyFormat';
import { FALLBACK_DELIVERY_CHARGE_PER_DAY } from '../../pages/MealPlans/subscriptionChargesUtils';
import { isBuilderSubscriptionPayload } from '../SubscriptionSummary/builderSubscriptionMeta';
import { resolveSubscriptionChargeTotals } from '../../utils/subscriptionChargeDisplay';
import {
  buildSlotPatternRows,
  formatSpecialtyWithRegion,
  resolveDietLabel,
  resolvePlanLabel,
  resolveRegionLabel,
} from '../../utils/subscriptionDisplay';

export const RECEIPT_DELIVERY_CHARGE_PER_DAY = FALLBACK_DELIVERY_CHARGE_PER_DAY;

export function formatReceiptCurrency(value = 0) {
  return formatMoneyInr(value);
}

function normalizeReceiptAddOns(addOns = []) {
  return (Array.isArray(addOns) ? addOns : [])
    .map((addon) => {
      const id = Number(addon?.id ?? addon?.addOnItemID ?? addon?.itemID);
      const quantity = Math.max(1, Math.round(Number(addon?.quantity) || 1));
      const price = Number(addon?.price) || 0;
      const name = String(addon?.name || '').trim() || 'Add-on';
      return {
        id: Number.isFinite(id) && id > 0 ? id : name,
        name,
        quantity,
        price,
        lineTotal: price * quantity,
      };
    })
    .filter((addon) => addon.name);
}

/** Build receipt line rows from snapshot / draft products (includes add-on qty). */
export function buildLineItems(products = []) {
  return (Array.isArray(products) ? products : []).map((product) => {
    const quantity = Number(product.quantity || 0);
    const includedInPlan = Boolean(product.includedInPlan);
    const billableQty = includedInPlan ? Math.max(0, quantity - 1) : quantity;
    const addOns = normalizeReceiptAddOns(product.addOns);
    const hostUnit = Number(
      product.hostUnitPrice != null ? product.hostUnitPrice : product.price || 0,
    ) || 0;
    const addOnTotal = addOns.reduce((sum, addon) => sum + addon.lineTotal, 0);
    const lineTotal = addOns.length
      ? (hostUnit * quantity) + addOnTotal
      : hostUnit * quantity;
    return {
      name: product.name || 'Item',
      quantity,
      price: hostUnit,
      billableQty,
      includedInPlan,
      lineTotal,
      addOns,
    };
  });
}

/**
 * Build the same receipt shape used after meal-plan create, from an account
 * subscription list/detail API row.
 */
export function buildInvoiceFromWebSubscription(subscription) {
  if (!subscription) return null;

  const snap = subscription.webPlanSnapshot || {};
  const user = subscription.subscriptionUserInformation || {};
  const address = subscription.subscriptionAddresses || {};
  const customer = snap.customer && typeof snap.customer === 'object' ? snap.customer : {};
  const products = Array.isArray(snap.products) ? snap.products : [];
  const totals = resolveSubscriptionChargeTotals(subscription);

  const fromLabel = formatDisplayDate(subscription.fromDate, '');
  const toLabel = formatDisplayDate(subscription.toDate, '');
  const dateRange = fromLabel
    ? `${fromLabel}${toLabel ? ` → ${toLabel}` : ''}`
    : '—';

  const lookupCode = subscription.lookupCode || snap.lookupCode || '';
  const customerName = (
    user.name
    || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
    || 'Customer'
  );
  const customerPhone = user.mobileNumber || customer.phone || '';
  const customerArea =
    customer.locationArea
    || address.city
    || customer.city
    || '';

  const specialtyLabel = formatSpecialtyWithRegion(subscription);
  const regionLabel = resolveRegionLabel(subscription);
  const mealTypeLabel = resolveDietLabel(subscription);
  const slotPatternRows = buildSlotPatternRows(subscription);
  const mealLabel = snap.mealLabel
    || (slotPatternRows.length
      ? slotPatternRows.map((r) => r.slotLabel).join(', ')
      : '')
    || (Array.isArray(snap.slotLabels) && snap.slotLabels.length
      ? snap.slotLabels.join(', ')
      : '')
    || 'Meal plan';

  const invoice = {
    referenceId: subscription.subscriptionNumber || String(subscription.id || ''),
    lookupCode,
    lookupUrl: snap.lookupUrl
      || (lookupCode ? `/track-order?code=${encodeURIComponent(lookupCode)}` : ''),
    qrCodeUrl: snap.qrCodeUrl || '',
    generatedAt: formatDisplayDateTime(subscription.created_at || new Date()),
    title: snap.title || '',
    planLabel: resolvePlanLabel(subscription, snap.planLabel || ''),
    specialtyLabel,
    regionLabel,
    mealTypeLabel,
    mealLabel,
    scheduleLabel: snap.scheduleLabel || '',
    scheduleId: snap.scheduleId || '',
    itemSchedules: Array.isArray(snap.itemSchedules) ? snap.itemSchedules : [],
    slotPatternRows,
    dateRange,
    customerName,
    customerPhone,
    customerArea,
    lineItems: buildLineItems(products),
    subtotal: totals.subTotal,
    gstAmount: totals.tax,
    packagingCharges: totals.packaging,
    deliveryCharges: totals.delivery,
    grandTotal: totals.grandTotal,
    originalGrandTotal: totals.originalGrandTotal,
    specialDiscountAmount: totals.specialDiscountAmount,
    perDayPrice: totals.perDayPrice,
    actualSubtotal: totals.actualSubtotal,
    discountAmount: totals.discountAmount,
    discountPercent: totals.discountPercent,
    deliveryDayCount: totals.days,
    isFreeDeliveryPlan: totals.isFreeDeliveryPlan,
    usedFallbackDelivery: Boolean(snap.charges?.usedFallbackDelivery ?? snap.usedFallbackDelivery),
    distanceKm: totals.distanceKm,
    gstPercent: totals.gstPercent,
    isEditing: false,
  };

  return {
    ...invoice,
    isBuilder: isBuilderSubscriptionPayload(invoice),
  };
}
